import React, { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { resolveAudio } from "../lib/api";
import { stopAllMediaExcept, onExclusivePlay } from "../lib/mediaBus";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export const AudioPlayer = ({ src, title, compact = false, testid = "audio-player" }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const t = () => setCur(a.currentTime);
    const d = () => setDur(a.duration);
    const e = () => setPlaying(false);
    a.addEventListener("timeupdate", t);
    a.addEventListener("loadedmetadata", d);
    a.addEventListener("ended", e);
    return () => {
      a.removeEventListener("timeupdate", t);
      a.removeEventListener("loadedmetadata", d);
      a.removeEventListener("ended", e);
    };
  }, [src]);

  const toggle = () => {
    const a = ref.current;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      stopAllMediaExcept(a);
      a.play(); setPlaying(true);
    }
  };
  useEffect(() => onExclusivePlay((el) => { if (el !== ref.current) setPlaying(false); }), []);
  const seek = (v) => { ref.current.currentTime = (v[0] / 100) * (dur || 1); setCur(ref.current.currentTime); };

  return (
    <div data-testid={testid}
      className={`flex items-center gap-4 ${compact ? "p-3" : "p-4"} rounded-md border transition-all duration-300 ${playing ? "border-[#C8A97E]/60 bg-[#C8A97E]/5" : "border-white/10 bg-[#12141A]"}`}>
      <audio ref={ref} src={resolveAudio(src)} preload="metadata" />
      <button onClick={toggle} data-testid={`${testid}-toggle`}
        className="shrink-0 h-11 w-11 rounded-full bg-[#C8A97E] text-[#0B0C10] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
        {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        {title && <div className="text-sm font-medium truncate mb-1.5">{title}</div>}
        <div className="flex items-center gap-3">
          {playing ? (
            <div className="flex items-end gap-0.5 h-4 w-5 shrink-0">
              {[0, 1, 2, 3].map((i) => <div key={i} className="eq-bar" style={{ animationDelay: `${i * 0.15}s` }} />)}
            </div>
          ) : null}
          <Slider value={[dur ? (cur / dur) * 100 : 0]} onValueChange={seek} max={100} step={0.1} className="flex-1" />
          <span className="font-mono text-xs text-[#A1A1AA] shrink-0 w-20 text-right">{fmt(cur)} / {fmt(dur)}</span>
        </div>
      </div>
    </div>
  );
};
